/* eslint-disable no-use-before-define */

import memoize from 'memoizee';
import BN from 'bignumber.js';

// Note: rounds if necessary
const trunc = (number, decimals = 0) => parseFloat(number).toFixed(decimals);

const truncWithoutRounding = (number, decimals = 0) => BN(number).toFixed(decimals, BN.ROUND_DOWN);

// Same as trunc(), but removes decimals if they were all 0
// E.g. trunc(55, 2) === '55.00'; truncWithoutZeroDecimals(55, 2) === '55'
const trailingZeroesRegex = /^.+?(\.?0*)$/;
const integerRegex = /^\d*$/;
const truncWithoutZeroDecimals = (number, decimals = 0) => {
    const trunced = trunc(number, decimals);
    if (integerRegex.test(trunced)) return trunced;

    const zeroesMatches = trailingZeroesRegex.exec(trunced);
    if (zeroesMatches !== null && zeroesMatches[1] !== '') {
        return trunced.slice(0, trunced.length - zeroesMatches[1].length);
    }
    return trunced;
};

const formatLargeNumber = (number, decimalCount = 2) => (
    number > 10 ** 9 ? `${localNumber(truncWithoutZeroDecimals(number / (10 ** 9), 1), undefined, decimalCount)}b` :
    number > 10 ** 6 ? `${localNumber(truncWithoutZeroDecimals(number / (10 ** 6), 1), undefined, decimalCount)}m` :
    number > 10 ** 5 ? `${localNumber(truncWithoutZeroDecimals(number / (10 ** 3), 1), undefined, decimalCount)}k` :
    number > 1 ? localNumber(truncWithoutZeroDecimals(number, decimalCount), undefined, decimalCount) :
    truncWithXSignificantDecimals(number)
);

// Use, in order of preference, depending on support:
// - Intl.NumberFormat.prototype.format: it's the most performant
// - Number.toLocaleString: does the same thing, but way slower
// - No formatting at all if no support
const getNumberFormatter = memoize((minimumFractionDigits, maximumFractionDigits) => (
    (window.Intl && window.Intl.NumberFormat) ?
        new Intl.NumberFormat(undefined, { minimumFractionDigits, maximumFractionDigits }).format :
        Number.toLocaleString ?
            (number) => (
                Number(number).toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits })
            ) :
            (number) => Number(number)
), { length: 2 });

const localNumber = (
    number,
    minimumFractionDigits = undefined,
    maximumFractionDigits = undefined
) => (
    getNumberFormatter(minimumFractionDigits, maximumFractionDigits)(number)
);

const truncWithXSignificantDecimals = (number, decimalCount = 2) => {
    const positiveNumber = Math.abs(number);
    let lownessThreshold = 0.1;
    let decimalPlaces = 1;

    while (positiveNumber < lownessThreshold && lownessThreshold > 1e-6) {
        lownessThreshold /= 10;
        decimalPlaces += 1;
    }

    return truncWithoutZeroDecimals(number, decimalPlaces + decimalCount - 1);
};

const numberToUint = (number, decimals) => {
  if (typeof number === 'undefined' || typeof decimals === 'undefined') {
    throw new Error('Missing some mandatory parameters for numberToUint(number, decimals)');
  }

  return BN(number).times(10 ** decimals).toFixed();
};

// Returns the decimals number, rather than the number of decimals
// E.g. 18 -> 1e18; noop if already a decimals number, e.g. 1e19 -> 1e18
const decimalsNumber = (decimals) => (
    decimals > 18 ? decimals : (10 ** decimals)
);

export {
    trunc,
    truncWithoutRounding,
    formatLargeNumber,
    truncWithoutZeroDecimals,
    truncWithXSignificantDecimals,
    localNumber,
    numberToUint,
    decimalsNumber,
};
